import React, { useEffect, useRef } from 'react';
// Import your screenshots here
import step1 from '../assets/step1.png';
import step2 from '../assets/step2.png';
import step3 from '../assets/step3.png';

const steps = [
  {
    num: '1',
    title: 'Open a software product',
    desc: "Inside your favourite software products or products you're new to, and trialling. Open Shepherd's browser plug-in.",
    image: step1,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 3v14M3 10h14" stroke="#1a73e8" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    num: '2',
    title: ' Ask a question',
    desc: "Open shepherd's browser plug-in and ask a question or select a frequently asked question to get in-app guidance.",
    image: step2,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7" stroke="#1a73e8" strokeWidth="1.6"/>
        <path d="M10 7v3l2 2" stroke="#1a73e8" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    num: '3',
    title: 'Follow an experts guide',
    desc: "Click the highlighted elements and follow the guide made by an expert creator on the page. Helping you save time learning how to use software or finding the answers.",
    image: step3,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 10l5 5 8-8" stroke="#1a73e8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

export default function HowItWorks() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add('visible'); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="daily-users" style={styles.section}>
      <style>{`
        .reveal-how { opacity:0; transform:translateY(20px); transition: opacity 0.6s ease, transform 0.6s ease; } 
        .reveal-how.visible { opacity:1; transform:none; }
      `}</style>
      <div style={styles.inner} ref={ref} className="reveal-how">
        <span style={styles.badge}>Daily users</span>
        <div style={styles.head}>
          <span style={styles.label}>How does it work?</span>
          <h2 style={styles.h2}>Get help without<br />searching for it</h2>
          <p style={styles.sub}>No back and forth on video tutorials. No confusing manuals, or outdated forums. Instant guidance without leaving the page.</p>
        </div>
        
        <div style={styles.grid}>
          {steps.map((step) => (
            <div key={step.num} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardNum}>{step.num}</div>
              </div>
              
              <h3 style={styles.cardTitle}>{step.title}</h3>
              <p style={styles.cardDesc}>{step.desc}</p>

              <div style={styles.imgContainer}>
                <img src={step.image} alt={step.title} style={styles.screenshot} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const styles = {
  section: { 
    background: 'var(--warm-cream)', 
    padding: "40px 20px",
  },
  inner: { 
    maxWidth: "1200px", 
    margin: '0 auto' 
  },
  head: { 
    marginBottom: '3.5rem',
    textAlign: 'left'
  },
  label: { 
    display: 'inline-block', 
    fontSize: '1rem', 
    fontWeight: 600, 
    color: 'var(--brand)', 
    textTransform: 'uppercase', 
    letterSpacing: '0.1em', 
    marginBottom: '0.8rem' 
  },
  badge: {
    backgroundColor: 'rgba(26, 115, 232, 0.1)',
    color: '#1a73e8',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    display: 'inline-block',
    marginBottom: '20px',
  },
  h2: { 
    fontFamily: "'Sora', sans-serif", 
    fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', 
    fontWeight: 700, 
    lineHeight: 1.15, 
    color: 'var(--charcoal)', 
    marginBottom: '1rem' 
  },
  sub: { 
    fontSize: '1.15rem', 
    color: 'var(--text-muted)', 
    maxWidth: 550, 
    fontWeight: 400,
    lineHeight: 1.5 
  },
  grid: { 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
    gap: '2rem' 
  },
  card: { 
    background: 'white', 
    borderRadius: '24px', 
    padding: '2rem', 
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  cardNum: { 
    fontFamily: "'Sora', sans-serif", 
    fontSize: '1.4rem', 
    fontWeight: 700, 
    color: 'var(--brand-pale)', 
    opacity: 0.8 
  },
  cardTitle: { 
    fontFamily: "'Sora', sans-serif", 
    fontSize: '1.25rem', 
    fontWeight: 700, 
    marginBottom: '0.8rem', 
    color: 'var(--charcoal)' 
  },
  cardDesc: { 
    fontSize: '1rem', 
    color: '#4b5563', 
    lineHeight: 1.6, 
    marginBottom: '2rem' 
  },
  imgContainer: {
    marginTop: 'auto',
    background: '#f8f9ff',
    borderRadius: '16px',
    padding: '10px',
    border: '1px solid #f0f2f8',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0,0,0,0.03)'
  },
  screenshot: {
    width: '100%',
    height: 'auto',
    borderRadius: '8px',
    display: 'block',
    objectFit: 'cover'
  }
};